/**
 * Sales Flow Page
 * Interactive visualization of the sales process for training and reference
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  BackgroundVariant,
  Panel,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useAuth } from '../utils/AuthContext';
import { getSettings, updateSetting, subscribeToSettings, SalesFlowData } from '../utils/appSettings';
import AppHeader from '../components/ui/AppHeader';
import { Save, Plus, Trash2, X, ArrowLeft } from 'lucide-react';
import './SalesFlowPage.css';

// Default flow for new installations
const getDefaultNodes = (): Node[] => [
  {
    id: '1',
    type: 'default',
    position: { x: 100, y: 50 },
    draggable: true,
    data: {
      label: 'Lead Intake',
      description: 'New lead enters via website, referral, or marketing',
      stepNumber: 1,
      details: [
        'Automated welcome SMS sent immediately',
        'Email sequence triggered (3-email drip)',
        'Lead assigned to sales rep based on territory',
        'Activity logged in CRM automatically',
      ],
    },
  },
  {
    id: '2',
    type: 'default',
    position: { x: 100, y: 150 },
    draggable: true,
    data: {
      label: 'Initial Qualification',
      description: 'Sales rep qualifies via messages and call attempts',
      stepNumber: 2,
      details: [
        'Review lead info in Messages panel',
        'Respond within 24hrs',
        'Ask qualifying questions',
        'Schedule discovery call',
      ],
    },
  },
  {
    id: '3',
    type: 'default',
    position: { x: 100, y: 250 },
    draggable: true,
    data: {
      label: 'Discovery Call',
      description: 'Understand project needs and determine fit',
      stepNumber: 3,
      details: [
        'Discuss project vision',
        'Understand timeline and budget',
        'Determine kit mod OR custom',
      ],
    },
  },
  {
    id: '4',
    type: 'default',
    position: { x: 100, y: 350 },
    draggable: true,
    data: {
      label: 'Project Type Decision',
      description: 'Kit modification or custom project?',
      stepNumber: 4,
      details: [
        'Kit Mod: modify existing design',
        'Custom: fully custom design work',
      ],
    },
  },
  {
    id: '4a',
    type: 'default',
    position: { x: -100, y: 450 },
    draggable: true,
    data: {
      label: 'Kit Mod ($500)',
      description: 'Send $500 engagement letter',
      details: ['Concept Payment letter', 'Simpler scope'],
    },
  },
  {
    id: '4b',
    type: 'default',
    position: { x: 300, y: 450 },
    draggable: true,
    data: {
      label: 'Custom ($1,600)',
      description: 'Send $1,600 concept design fee',
      details: ['Concept Design Fee letter', 'Includes 2D plans'],
    },
  },
  {
    id: '5',
    type: 'default',
    position: { x: 100, y: 550 },
    draggable: true,
    data: {
      label: 'Payment Received',
      description: 'Engagement signed and paid',
      stepNumber: 5,
      details: ['Payment via QuickBooks', 'Schedule design meeting'],
    },
  },
  {
    id: '6',
    type: 'default',
    position: { x: 100, y: 650 },
    draggable: true,
    data: {
      label: 'Design Handoff',
      description: 'Transfer from Sales to Ops/Arch team',
      stepNumber: 6,
      details: ['Meeting with Client, Sales, Architect, PM'],
    },
  },
  {
    id: '7',
    type: 'default',
    position: { x: 100, y: 750 },
    draggable: true,
    data: {
      label: 'Design Delivery',
      description: 'Arch team delivers design',
      stepNumber: 7,
      details: ['Kit: 3D model', 'Custom: full drawings'],
    },
  },
  {
    id: '8',
    type: 'default',
    position: { x: 100, y: 850 },
    draggable: true,
    data: {
      label: 'Proposal & Quote',
      description: 'Sales creates timber frame quote',
      stepNumber: 8,
      details: ['Use model for material takeoff', 'Present proposal'],
    },
  },
  {
    id: '9',
    type: 'default',
    position: { x: 100, y: 950 },
    draggable: true,
    data: {
      label: 'Contract Signed',
      description: 'Project moves to production',
      stepNumber: 9,
      details: ['Deal marked Won', 'Handed to Operations'],
    },
  },
];

const getDefaultEdges = (): Edge[] => [
  { id: 'e1-2', source: '1', target: '2', type: 'smoothstep' },
  { id: 'e2-3', source: '2', target: '3', type: 'smoothstep' },
  { id: 'e3-4', source: '3', target: '4', type: 'smoothstep' },
  { id: 'e4-4a', source: '4', target: '4a', label: 'Kit Mod', type: 'smoothstep' },
  { id: 'e4-4b', source: '4', target: '4b', label: 'Custom', type: 'smoothstep' },
  { id: 'e4a-5', source: '4a', target: '5', type: 'smoothstep' },
  { id: 'e4b-5', source: '4b', target: '5', type: 'smoothstep' },
  { id: 'e5-6', source: '5', target: '6', type: 'smoothstep' },
  { id: 'e6-7', source: '6', target: '7', type: 'smoothstep' },
  { id: 'e7-8', source: '7', target: '8', type: 'smoothstep' },
  { id: 'e8-9', source: '8', target: '9', type: 'smoothstep' },
];

interface SelectedNodeData {
  id: string;
  label: string;
  description: string;
  details: string[];
  stepNumber?: number;
  position: { x: number; y: number };
}

// Inner component that uses useReactFlow
const SalesFlowInner: React.FC = () => {
  const { user } = useAuth();
  const { fitView, setCenter, getZoom } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Detail view state
  const [selectedNode, setSelectedNode] = useState<SelectedNodeData | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<SelectedNodeData | null>(null);
  const [newDetailText, setNewDetailText] = useState('');

  const prevZoomRef = useRef<{ x: number; y: number; zoom: number } | null>(null);

  const isAdmin = user?.role === 'admin';

  // Load saved flow or use defaults
  useEffect(() => {
    const loadFlow = async () => {
      try {
        const settings = await getSettings();
        if (settings.salesFlow?.nodes && settings.salesFlow.nodes.length > 0) {
          const loadedNodes = settings.salesFlow.nodes.map((n: any) => ({
            ...n,
            type: 'default',
            draggable: true,
          }));
          setNodes(loadedNodes as Node[]);
          setEdges(settings.salesFlow.edges.map((e: any) => ({ ...e, type: 'smoothstep' })) as Edge[]);
        } else {
          setNodes(getDefaultNodes());
          setEdges(getDefaultEdges());
        }
      } catch (error) {
        console.error('Error loading sales flow:', error);
        setNodes(getDefaultNodes());
        setEdges(getDefaultEdges());
      } finally {
        setLoading(false);
      }
    };

    loadFlow();
  }, [setNodes, setEdges]);

  // Handle node click - zoom in and show details
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    // Store current view position
    prevZoomRef.current = {
      x: node.position.x,
      y: node.position.y,
      zoom: getZoom(),
    };

    const nodeData: SelectedNodeData = {
      id: node.id,
      label: node.data.label || '',
      description: node.data.description || '',
      details: node.data.details || [],
      stepNumber: node.data.stepNumber,
      position: node.position,
    };

    setSelectedNode(nodeData);
    setEditData(nodeData);

    // Zoom to the node (offset to the left to make room for details)
    setTimeout(() => {
      setCenter(node.position.x + 100, node.position.y + 30, { zoom: 1.5, duration: 500 });
    }, 50);

    // Show details after zoom animation
    setTimeout(() => {
      setShowDetails(true);
    }, 400);
  }, [setCenter, getZoom]);

  // Close details and zoom back out
  const handleCloseDetails = useCallback(() => {
    setShowDetails(false);
    setEditMode(false);

    setTimeout(() => {
      fitView({ duration: 400, padding: 0.2 });
      setSelectedNode(null);
      setEditData(null);
    }, 200);
  }, [fitView]);

  // Track position changes
  const handleNodesChange = useCallback((changes: any) => {
    onNodesChange(changes);
    if (changes.some((c: any) => c.type === 'position' && c.dragging === false)) {
      setHasChanges(true);
    }
  }, [onNodesChange]);

  // Handle connections
  const onConnect = useCallback(
    (params: Connection) => {
      if (isAdmin) {
        setEdges((eds) => addEdge({ ...params, type: 'smoothstep' }, eds));
        setHasChanges(true);
      }
    },
    [isAdmin, setEdges]
  );

  // Add new node
  const handleAddNode = useCallback(() => {
    const newId = `node-${Date.now()}`;
    const newNode: Node = {
      id: newId,
      type: 'default',
      position: { x: 200, y: 100 },
      draggable: true,
      data: {
        label: 'New Step',
        description: 'Click to edit',
        details: [],
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setHasChanges(true);
  }, [setNodes]);

  // Delete node
  const handleDeleteNode = useCallback(() => {
    if (!selectedNode) return;
    if (window.confirm('Delete this node?')) {
      setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
      setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
      handleCloseDetails();
      setHasChanges(true);
    }
  }, [selectedNode, setNodes, setEdges, handleCloseDetails]);

  // Save edits to node
  const handleSaveEdit = useCallback(() => {
    if (!editData) return;

    setNodes((nds) => nds.map((n) => {
      if (n.id === editData.id) {
        return {
          ...n,
          data: {
            ...n.data,
            label: editData.label,
            description: editData.description,
            details: editData.details,
            stepNumber: editData.stepNumber,
          },
        };
      }
      return n;
    }));

    setSelectedNode(editData);
    setHasChanges(true);
    setEditMode(false);
  }, [editData, setNodes]);

  // Add detail
  const handleAddDetail = useCallback(() => {
    if (!newDetailText.trim() || !editData) return;
    setEditData({
      ...editData,
      details: [...editData.details, newDetailText.trim()],
    });
    setNewDetailText('');
  }, [newDetailText, editData]);

  // Remove detail
  const handleRemoveDetail = useCallback((index: number) => {
    if (!editData) return;
    setEditData({
      ...editData,
      details: editData.details.filter((_, i) => i !== index),
    });
  }, [editData]);

  // Save flow to Firestore
  const handleSave = async () => {
    if (!hasChanges) return;

    setSaving(true);
    try {
      const flowData: SalesFlowData = {
        nodes: nodes.map(node => ({
          id: node.id,
          type: 'default' as any,
          position: node.position,
          data: {
            label: node.data.label,
            description: node.data.description,
            details: node.data.details,
            stepNumber: node.data.stepNumber,
          },
        })),
        edges: edges.map(edge => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          label: edge.label as string | undefined,
        })),
        updatedAt: new Date(),
        updatedBy: user?.email || 'unknown',
      };

      await updateSetting('salesFlow', flowData);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving sales flow:', error);
      alert('Error saving');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-state">Loading...</div>;
  }

  return (
    <>
      <div className={`flow-container ${showDetails ? 'details-open' : ''}`}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={isAdmin ? handleNodesChange : undefined}
          onEdgesChange={isAdmin ? onEdgesChange : undefined}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodesDraggable={isAdmin}
          nodesConnectable={isAdmin}
          panOnDrag={!showDetails}
          zoomOnScroll={!showDetails}
          fitView
          fitViewOptions={{ padding: 0.3 }}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e2e8f0" />
          <Controls showInteractive={false} />
          <MiniMap nodeColor="#3b82f6" maskColor="rgba(255,255,255,0.8)" />

          {/* Admin Controls */}
          {isAdmin && !showDetails && (
            <Panel position="top-right" className="admin-panel">
              <button className="admin-btn add-btn" onClick={handleAddNode}>
                <Plus size={18} />
                <span>Add</span>
              </button>
              <button
                className="admin-btn save-btn"
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                <Save size={18} />
                <span>{saving ? '...' : hasChanges ? 'Save' : 'Saved'}</span>
              </button>
            </Panel>
          )}
        </ReactFlow>
      </div>

      {/* Detail Panel - slides out from behind node */}
      {selectedNode && (
        <div className={`detail-overlay ${showDetails ? 'visible' : ''}`}>
          <div className="detail-card">
            {/* Back button */}
            <button className="back-btn" onClick={handleCloseDetails}>
              <ArrowLeft size={20} />
              <span>Back to Flow</span>
            </button>

            {editMode && editData ? (
              // Edit Mode
              <div className="detail-content edit-mode">
                <div className="field">
                  <label>Title</label>
                  <input
                    type="text"
                    value={editData.label}
                    onChange={(e) => setEditData({ ...editData, label: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Description</label>
                  <textarea
                    value={editData.description}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="field">
                  <label>Step Number</label>
                  <input
                    type="number"
                    value={editData.stepNumber || ''}
                    onChange={(e) => setEditData({
                      ...editData,
                      stepNumber: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                    min="1"
                  />
                </div>
                <div className="field">
                  <label>Details</label>
                  <div className="details-list">
                    {editData.details.map((d, i) => (
                      <div key={i} className="detail-item">
                        <span>{d}</span>
                        <button onClick={() => handleRemoveDetail(i)}><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="add-detail-row">
                    <input
                      value={newDetailText}
                      onChange={(e) => setNewDetailText(e.target.value)}
                      placeholder="Add detail..."
                      onKeyDown={(e) => e.key === 'Enter' && handleAddDetail()}
                    />
                    <button onClick={handleAddDetail}><Plus size={16} /></button>
                  </div>
                </div>
                <div className="edit-actions">
                  <button className="cancel-btn" onClick={() => setEditMode(false)}>Cancel</button>
                  <button className="save-edit-btn" onClick={handleSaveEdit}>Save</button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="detail-content">
                {selectedNode.stepNumber && (
                  <div className="step-number">Step {selectedNode.stepNumber}</div>
                )}
                <h2>{selectedNode.label}</h2>
                <p className="description">{selectedNode.description}</p>

                {selectedNode.details.length > 0 && (
                  <div className="details-section">
                    <h4>Details</h4>
                    <ul>
                      {selectedNode.details.map((detail, i) => (
                        <li key={i}>{detail}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {isAdmin && (
                  <div className="admin-actions">
                    <button className="edit-btn" onClick={() => setEditMode(true)}>
                      Edit
                    </button>
                    <button className="delete-btn" onClick={handleDeleteNode}>
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Main component with provider
const SalesFlowPage: React.FC = () => {
  return (
    <div className="sales-flow-page">
      <AppHeader />
      <ReactFlowProvider>
        <SalesFlowInner />
      </ReactFlowProvider>
    </div>
  );
};

export default SalesFlowPage;
